export type LayoutStyle =
    | 'default'
    | 'cinematic'
    | 'centered'
    | 'left_aligned'
    | 'split'
    | 'grid_layout'
    | 'background_cards'
    | 'horizontal_scroll'
    | 'pill_list'
    | 'minimal_list'
    | 'dark_grid'
    | 'light_grid'
    | 'channel_background_cards'
    | 'standard_cards'
    | 'icon_cards'
    | 'large_feature_cards'
    | 'single_hero'
    | 'three_col';

export const LAYOUT_STYLE_LABELS: Record<LayoutStyle, string> = {
    default: 'Default',
    cinematic: 'Cinematic',
    centered: 'Centered',
    left_aligned: 'Left aligned',
    split: 'Split (text + image)',
    grid_layout: 'Grid',
    background_cards: 'Background cards',
    horizontal_scroll: 'Horizontal scroll',
    pill_list: 'Pill list',
    minimal_list: 'Minimal list',
    dark_grid: 'Dark grid',
    light_grid: 'Light grid',
    channel_background_cards: 'Background cards',
    standard_cards: 'Standard cards',
    icon_cards: 'Icon cards',
    large_feature_cards: 'Large feature cards',
    single_hero: 'Single hero',
    three_col: 'Three column',
};

export type ContentSourceType =
    | 'custom'
    | 'trending'
    | 'featured'
    | 'recent'
    | 'channel'
    | 'category'
    | 'courses'
    | 'resources';

export interface ContentSource {
    type: ContentSourceType;
    limit?: number;
}

export interface ContentRow {
    id: string;
    type: string;
    title?: string | null;
    subtitle?: string | null;
    layoutStyle?: LayoutStyle;
    settings?: Record<string, unknown>;
    content?: any[];
}

export interface NavLinkConfig {
    key: string;
    label: string;
    url: string;
    children?: NavLinkConfig[];
}
export interface FooterColumnConfig {
    key: string;
    title: string;
    links: NavLinkConfig[];
}
export interface SocialLinkConfig {
    key: string;
    label: string;
    url: string;
}

export interface AccountMenuItemConfig {
    key: string;
    label: string;
    url: string;
    icon: string;
}

export interface AdminBlockConfig extends ContentRow {
    sortOrder: number;
    enabled: boolean;
    contentSource: ContentSource;
}

export interface BlockDefinition {
    type: string;
    label: string;
    icon: string;
    description: string;
    supportedLayouts: LayoutStyle[];
}

export const BLOCK_DEFINITIONS: BlockDefinition[] = [
    // Banners
    { type: 'welcome-banner', label: 'Welcome Banner', icon: '👋', description: 'Hero-style welcome message with CTA buttons', supportedLayouts: ['centered', 'left_aligned', 'split'] },
    { type: 'hero-carousel', label: 'Hero Carousel', icon: '🎬', description: 'Full-width hero banner with auto-sliding content', supportedLayouts: ['default', 'cinematic'] },

    // Categories
    { type: 'categories-list', label: 'Categories List', icon: '📂', description: 'Browsable list of categories', supportedLayouts: ['grid_layout', 'background_cards', 'horizontal_scroll', 'pill_list', 'minimal_list'] },
    { type: 'highlighted-categories', label: 'Highlighted Categories', icon: '📁', description: 'Featured category tiles or pills', supportedLayouts: ['dark_grid', 'light_grid', 'pill_list'] },

    // Channels
    { type: 'all-channels', label: 'All Channels', icon: '📺', description: 'Grid or scroll of all channels', supportedLayouts: ['channel_background_cards', 'standard_cards', 'minimal_list', 'icon_cards', 'large_feature_cards'] },
    { type: 'featured-channel', label: 'Featured Channel', icon: '🎯', description: 'Single channel hero with its videos below', supportedLayouts: ['default'] },

    // Content rows
    { type: 'medium-carousel', label: 'Video Carousel (Medium)', icon: '🎞️', description: 'Scrollable row of video/course/resource cards', supportedLayouts: ['default'] },
    { type: 'search-filter', label: 'Search & Filter Bar', icon: '🔍', description: 'Search box with dropdown filters', supportedLayouts: ['default'] },
    { type: 'quick-links', label: 'Quick Links', icon: '🔗', description: 'Row of quick-access link cards', supportedLayouts: ['default'] },
    { type: 'text-over-video', label: 'Featured Collections', icon: '📝', description: 'Text overlaid on thumbnail cards', supportedLayouts: ['default', 'single_hero', 'three_col'] },
    { type: 'thumbnail-grid', label: 'Thumbnail Grid', icon: '🖼️', description: 'Grid of small thumbnails', supportedLayouts: ['default'] },
    { type: 'vertical-list', label: 'Vertical List', icon: '📋', description: 'Ranked vertical list of content', supportedLayouts: ['default'] },
    { type: 'horizontal-scroll', label: 'Horizontal Scroll', icon: '↔️', description: 'Square-thumbnail horizontal scroller', supportedLayouts: ['default'] },
    { type: 'custom-promo', label: 'Custom HTML Block', icon: '🛠️', description: 'Raw HTML/CSS/JS block', supportedLayouts: ['default'] },
    {
        type: 'header',
        label: 'Header',
        icon: '🧭',
        description: 'Site header with logo, navigation links, search bar, action buttons, and account menu — fully customizable.',
        supportedLayouts: ['default', 'centered'] as LayoutStyle[],
    },
    {
        type: 'footer',
        label: 'Footer',
        icon: '🌐',
        description: 'Site footer with logo, copyright, link columns, and social icons.',
        supportedLayouts: ['default'] as LayoutStyle[],
    }
];

export interface VideoContent {
    id: string;
    title: string;
    thumbnail?: string;
    duration?: string;
    views?: number;
    channelName?: string;
    description?: string;
}